import { useState, useCallback, useEffect } from "react";
import { Plus, Trash2, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

/**
 * Equipment Section - handles equipment line items
 * Includes table display and add/edit dialog
 * Unit is auto-populated from the selected equipment item in Settings
 */
export const EquipmentSection = ({
  equipmentItems,
  equipment, // from settings
  canManageJobs,
  formatCurrency,
  onAddItem,
  onEditItem,
  onDeleteItem,
  equipmentSubtotal,
  preSelectItem,
  onPreSelectHandled,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [form, setForm] = useState({
    category: "",
    item_id: "",
    quantity: "1",
    cost_type: "unit_price",
    unit_price: "",
    material_cost: "",
    labor_cost: "",
    notes: "",
  });

  // Get unique categories from settings
  const availableCategories = [...new Set(equipment.map(e => e.category))].filter(Boolean);
  
  // Filter equipment by selected category
  const filteredEquipment = form.category 
    ? equipment.filter(e => e.category === form.category)
    : [];

  const resetForm = useCallback(() => {
    setForm({
      category: "",
      item_id: "",
      quantity: "1",
      cost_type: "unit_price",
      unit_price: "",
      material_cost: "",
      labor_cost: "",
      notes: "",
    });
    setEditingItem(null);
  }, []);

  const openDialog = useCallback((item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        category: item.category || "",
        item_id: item.item_id || "",
        quantity: item.quantity?.toString() || "1",
        cost_type: item.cost_type || "unit_price",
        unit_price: item.unit_price?.toFixed(2) || "",
        material_cost: item.material_cost ? (item.material_cost / item.quantity).toFixed(2) : "",
        labor_cost: item.labor_cost ? (item.labor_cost / item.quantity).toFixed(2) : "",
        notes: item.notes || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  }, [resetForm]);

  // Auto-open dialog when preSelectItem is set from universal search
  useEffect(() => {
    if (preSelectItem) {
      const equip = equipment.find(e => e.id === preSelectItem.id);
      if (equip) {
        setEditingItem(null);
        setForm({
          category: equip.category || "",
          item_id: equip.id,
          quantity: "1",
          cost_type: "unit_price",
          unit_price: equip.unit_price ? equip.unit_price.toString() : "",
          material_cost: "",
          labor_cost: "",
          notes: "",
        });
        setDialogOpen(true);
      }
      if (onPreSelectHandled) onPreSelectHandled();
    }
  }, [preSelectItem, equipment, onPreSelectHandled]);

  const handleItemSelect = useCallback((itemId) => {
    const selectedItem = equipment.find(e => e.id === itemId);
    if (selectedItem) {
      setForm(f => ({
        ...f,
        item_id: itemId,
        unit_price: selectedItem.unit_price ? selectedItem.unit_price.toString() : "",
      }));
    }
  }, [equipment]);

  const handleSubmit = useCallback(() => {
    const selectedEquipment = equipment.find(e => e.id === form.item_id);
    if (!selectedEquipment) return;

    const quantity = parseFloat(form.quantity) || 1;
    const unit = selectedEquipment.unit || "each"; // Auto-populate unit from equipment definition
    let total = 0;
    let materialCost = 0;
    let laborCost = 0;

    if (form.cost_type === "unit_price") {
      const unitPrice = parseFloat(form.unit_price) || selectedEquipment.unit_price || 0;
      total = quantity * unitPrice;
    } else {
      materialCost = (parseFloat(form.material_cost) || 0) * quantity;
      laborCost = (parseFloat(form.labor_cost) || 0) * quantity;
    }

    const newItem = {
      id: editingItem?.id || crypto.randomUUID(),
      category: form.category,
      item_id: form.item_id,
      item_name: selectedEquipment.name,
      quantity,
      unit,
      cost_type: form.cost_type,
      unit_price: form.cost_type === "unit_price" ? (parseFloat(form.unit_price) || selectedEquipment.unit_price || 0) : 0,
      total,
      material_cost: materialCost,
      labor_cost: laborCost,
      notes: form.notes,
    };

    if (editingItem) {
      onEditItem(newItem);
    } else {
      onAddItem(newItem);
    }

    setDialogOpen(false);
    resetForm();
  }, [form, equipment, editingItem, onAddItem, onEditItem, resetForm]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl flex items-center gap-3 text-slate-700">
            <span>Equipment</span>
            <span className="text-muted-foreground font-normal">&ndash;</span>
            <span className="text-xl font-normal text-slate-500">Subtotal</span>
            <span className="font-mono font-bold text-2xl text-slate-700" data-testid="equipment-subtotal">{formatCurrency(equipmentSubtotal)}</span>
          </CardTitle>
          {canManageJobs && (
            <Button size="lg" className="text-lg px-6" onClick={() => openDialog()} data-testid="add-equipment-item-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {equipmentItems.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>No equipment added yet</p>
              <p className="text-sm mt-1">Click &quot;Add Item&quot; to add equipment from your list</p>
            </div>
          ) : (
            <div>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="bg-slate-100 border-b-2 border-slate-200">
                    <TableHead className="h-8 py-1 text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Equipment</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Qty</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Unit</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Unit Price</TableHead>
                    <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Cost</TableHead>
                    {canManageJobs && <TableHead className="h-8 py-1 w-20"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equipmentItems.map((item, idx) => {
                    const equipmentCost = item.cost_type === "unit_price" ? item.total : item.material_cost;
                    return (
                      <TableRow key={item.id} className="border-b border-slate-100">
                        <TableCell className="py-3">
                          <div>
                            <span className="font-medium">{item.item_name}</span>
                            {item.category && <span className="text-xs text-muted-foreground ml-2">({item.category})</span>}
                            {item.cost_type === "material_labor" && (
                              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Equip+Labor</span>
                            )}
                            {item.notes && <p className="text-xs text-muted-foreground italic">{item.notes}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{item.unit}</TableCell>
                        <TableCell className="text-right font-mono">{item.unit_price > 0 ? formatCurrency(item.unit_price) : "-"}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{equipmentCost > 0 ? formatCurrency(equipmentCost) : "-"}</TableCell>
                        {canManageJobs && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDeleteItem(item.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Equipment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit" : "Add"} Equipment Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <Label>Category *</Label>
              <Select 
                value={form.category} 
                onValueChange={(v) => setForm(f => ({...f, category: v, item_id: "", unit_price: "", unit: ""}))}
              >
                <SelectTrigger data-testid="equipment-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {[...availableCategories].sort((a, b) => a.localeCompare(b)).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableCategories.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No categories found. Add equipment in Settings first.</p>
              )}
            </div>
            <div className="form-group">
              <Label>Equipment Item *</Label>
              <Select 
                value={form.item_id} 
                onValueChange={handleItemSelect}
                disabled={!form.category}
              >
                <SelectTrigger data-testid="equipment-item-select">
                  <SelectValue placeholder={form.category ? "Select equipment" : "Select category first"} />
                </SelectTrigger>
                <SelectContent>
                  {[...filteredEquipment].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} {item.user_defined_price ? "(Variable)" : `(${formatCurrency(item.unit_price)}/${item.unit})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.category && filteredEquipment.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No equipment in this category. Add items in Settings.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Quantity *</Label>
                <NumericInput 
                  decimals={2}
                  min={0}
                  value={form.quantity} 
                  onChange={(e) => setForm(f => ({...f, quantity: e.target.value}))} 
                  placeholder="1"
                  data-testid="equipment-quantity-input"
                />
              </div>
              <div className="form-group">
                <Label>Unit</Label>
                <Input 
                  value={equipment.find(e => e.id === form.item_id)?.unit || "—"}
                  disabled
                  className="bg-muted"
                  data-testid="equipment-unit-display"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-set from equipment definition</p>
              </div>
            </div>
            <div className="form-group">
              <Label>Cost Type *</Label>
              <Select value={form.cost_type} onValueChange={(v) => setForm(f => ({...f, cost_type: v, unit_price: "", material_cost: "", labor_cost: ""}))}>
                <SelectTrigger data-testid="equipment-cost-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit_price">Unit Price</SelectItem>
                  <SelectItem value="material_labor">Equipment &amp; Labor Costs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.cost_type === "unit_price" ? (
              <>
                <div className="form-group">
                  <Label>Unit Price ($) *</Label>
                  <NumericInput 
                    decimals={2}
                    min={0}
                    value={form.unit_price} 
                    onChange={(e) => setForm(f => ({...f, unit_price: e.target.value}))} 
                    placeholder="0.00"
                    data-testid="equipment-unit-price-input"
                  />
                </div>
                {form.quantity && form.unit_price && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-800 font-medium">Line Total:</span>
                      <span className="text-lg font-bold text-blue-700">
                        {formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.unit_price) || 0))}
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">→ Goes to Equipment Subtotal</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Equipment Cost per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.material_cost} 
                      onChange={(e) => setForm(f => ({...f, material_cost: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="equipment-material-cost-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Labor Cost per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.labor_cost} 
                      onChange={(e) => setForm(f => ({...f, labor_cost: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="equipment-labor-cost-input"
                    />
                  </div>
                </div>
                {form.quantity && (form.material_cost || form.labor_cost) && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-1">
                    {form.material_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Equipment Total:</span>
                        <span className="font-bold text-green-700">{formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.material_cost) || 0))} → Equipment</span>
                      </div>
                    )}
                    {form.labor_cost && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Labor Total:</span>
                        <span className="font-bold text-green-700">{formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.labor_cost) || 0))} → Labor</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="form-group">
              <Label>Notes</Label>
              <Textarea 
                value={form.notes} 
                onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} 
                rows={2} 
                placeholder="Optional notes..." 
              />
            </div>
            <Button 
              onClick={handleSubmit} 
              className="w-full" 
              disabled={!form.item_id || !form.quantity || (form.cost_type === "unit_price" ? !form.unit_price : (!form.material_cost && !form.labor_cost))} 
              data-testid="add-equipment-submit-btn"
            >
              {editingItem ? "Save Changes" : "Add Equipment Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EquipmentSection;
